import { createSelector } from 'reselect'
import { fromJS, List, Map } from 'immutable'
import moment from 'moment'
import { types as constantTypes } from 'app/constants/notificationType'
import { NOTIFICATION_HOURS_DEFAULTS } from 'app/constants/notificationHours'

import {
  getActionType,
  getActivityId,
  getActivityName,
  getActivityQuestion,
  getHasStory,
  getIsRated,
} from 'store/entities/activity/selectors'
import NAV_LINKS from 'app/constants/navLinks'
import { SUB_LINKS_TAB } from 'app/constants/microactions'
import * as STAGES from 'app/constants/stages'
import { COMMIT, DID, DIDNOT } from 'app/constants/identityActions'
import { parseNotificationTypesBeforeShow } from 'helpers/notificationType'
import { parsePhoneBeforeShow } from 'helpers/parsePhone'
import { ADMIN, MULTI_ADMIN, SUPER_ADMIN } from 'app/constants/roles'

export const getIdentity = state => state.get('persist').identity

export const getIdentityAuthToken = createSelector(getIdentity, identity => identity.get('authToken'))

export const getIdentityAccessToken = createSelector(getIdentityAuthToken, authToken => authToken.get('accessToken'))

export const getIdentityLastUserActivity = createSelector(getIdentity, identity => identity.get('lastActivity'))

export const getIdentityName = createSelector(getIdentity, identity => identity.getName())

export const getIdentityLastUserActivityData = createSelector(getIdentityLastUserActivity, lastUserActivity =>
  Map(lastUserActivity).delete('proTrackLevelId')
)

export const getIdentityRoles = createSelector(getIdentity, identity => {
  const roles = identity.get('roles')
  const isSuperAdminRoleExists = roles.some(roleObj => roleObj.get('name') === SUPER_ADMIN)
  if (isSuperAdminRoleExists) {
    return roles.filter(roleObj => roleObj.get('name') !== MULTI_ADMIN)
  }
  return roles
})

export const getIdentityActiveRole = createSelector(getIdentity, identity => identity.get('activeRole'))

export const getIdentityActiveRoleName = createSelector(
  getIdentityRoles,
  getIdentityActiveRole,
  (roles, activeRole) => (roles && roles.get(activeRole) && roles.get(activeRole).get('name')) || null
)

export const isSuperAdmin = createSelector(getIdentityActiveRoleName, role => role === SUPER_ADMIN)

export const isAdmin = createSelector(getIdentityActiveRoleName, role => role === ADMIN)

export const isMultiAdmin = createSelector(getIdentityActiveRoleName, role => role === MULTI_ADMIN)

export const isSuperOrMultiAdmin = createSelector(
  [isSuperAdmin, isMultiAdmin],
  (isSuperAdmin, isMultiAdmin) => isSuperAdmin || isMultiAdmin
)

export const getSubLinksTab = createSelector(getIdentityActiveRoleName, role => SUB_LINKS_TAB[role])

export const getNavLinksForIdentity = createSelector(getIdentityActiveRoleName, role => NAV_LINKS.get(role))

export const getNavLinksForIdentityAsArray = createSelector(
  getNavLinksForIdentity,
  links => (links ? links.toArray().map(link => link.toJS()) : [])
)

export const getIdentityFirstLogin = createSelector(getIdentity, identity => identity.get('firstLogin'))

export const getIdentityFirstLoginFlow = createSelector(getIdentity, identity => identity.get('isLoginFlowFinished'))

export const getIdentityPreviousProTrackLevel = createSelector(getIdentity, identity =>
  identity.get('previousProTrackLevel')
)

export const getIdentityOrganizationId = createSelector(getIdentity, identity => identity.get('organizationId'))

export const getTimePreferences = createSelector(getIdentity, identity => {
  const notificationType = identity.get('notificationType')
  const iso = identity.get('phoneISO')
  return fromJS({
    commitNotificationTime: identity.get('commitNotificationTime') || NOTIFICATION_HOURS_DEFAULTS.MORNING,
    committedNotificationTime: identity.get('committedNotificationTime') || NOTIFICATION_HOURS_DEFAULTS.EVENING,
    timezone: identity.getTimezone(),
    phone: parsePhoneBeforeShow(identity.get('phone'), iso),
    phoneISO: iso,
    notificationType: (notificationType && parseNotificationTypesBeforeShow(notificationType)) || constantTypes.BOTH,
  })
})

const getIdentityNotificationType = createSelector(getIdentity, identity => identity.get('notificationType'))

export const isIdentityHasSmsNotificationType = createSelector(getIdentityNotificationType, notificationType =>
  notificationType.get('sms')
)

export const getIdentityUserData = createSelector(getIdentity, getTimePreferences, (identity, timePreferencesMap) => {
  const dayoffs = identity.get('dayoffs')
  const timePreferences = timePreferencesMap.toJS()
  return Map({
    id: identity.get('id'),
    firstName: identity.get('firstName'),
    lastName: identity.get('lastName'),
    avatar: identity.get('avatar'),
    email: identity.get('email'),
    phone: identity.get('phone'),
    phoneISO: identity.get('phoneISO'),
    dayoffs:
      dayoffs && dayoffs.map
        ? dayoffs.map(el =>
            Map({
              startDate: moment(el.get('startDate')),
              endDate: moment(el.get('endDate')),
            })
          )
        : List(),
    organizationId: identity.get('organizationId'),
    ...timePreferences,
  })
})

export const getTimeNotifications = createSelector([getIdentity, getTimePreferences], (identity, timePreferences) =>
  timePreferences.set('workWeek', identity.get('workWeek'))
)

export const getIdentityOrganization = createSelector(getIdentity, identity => identity.get('organization'))

export const getIdentityOrganizationName = createSelector(getIdentityOrganization, org => org.get('name'))

export const getOrganizationLanguage = createSelector(getIdentityOrganization, org => org.get('language'))

export const getIdentityTeams = createSelector(getIdentity, identity => identity.get('teams'))

export const getIdentityCanRepeatActivity = createSelector(
  getIdentityOrganization,
  organization => (organization ? organization.get('canRepeatActivity') : false)
)

export const getIdentityOrganizationLevelEndsByActivityCount = createSelector(
  getIdentityOrganization,
  organization => (organization ? organization.get('proTrackLevelEndsByActivityCount') : false)
)

export const getIdentityOrganizationDemoMode = createSelector(getIdentityOrganization, organization =>
  organization.get('demoMode')
)

export const getIdentityOrganizationTeamOnly = createSelector(getIdentityOrganization, organization =>
  organization.get('teamOnly')
)

export const hasAbilityToSelectNewProTrack = createSelector(
  getIdentityPreviousProTrackLevel,
  getIdentityOrganizationDemoMode,
  (previousProTrackLevel, isDemoMode) => !(isDemoMode && previousProTrackLevel.get('finished'))
)

export const getIdentityInitials = createSelector(getIdentity, identity => identity.getInitials())

export const getIdentityEmail = createSelector(getIdentity, identity => identity.get('email'))

export const getIdentityPhone = createSelector(getIdentity, identity => identity.get('phone'))

export const getIdentityPhoneCode = createSelector(getIdentity, identity => identity.get('phoneCode'))

export const getIdentityAvatar = createSelector(getIdentity, identity => identity.get('avatar'))

export const getIdentityDay = createSelector(getIdentity, identity => identity.get('day'))

export const getIdentityStage = createSelector(getIdentity, identity => identity.get('stage'))

export const isStageIsSelection = createSelector(getIdentityStage, stage => stage === STAGES.SELECTION)

export const isStageIsDone = createSelector(getIdentityStage, stage => stage === STAGES.DONE)

export const getIdentityDaysOff = createSelector(getIdentity, identity => identity.get('dayoffs'))

export const getIdentityCanCompletePreviousActivity = createSelector(
  getIdentity,
  identity => identity.get('canCompletePreviousActivity') && identity.get('lastActivity') !== null
)

export const getIdentityTimezone = createSelector(getIdentity, identity => identity.get('timezone'))

export const getIdentityActions = createSelector(getIdentity, identity => identity.get('actions'))

export const isActionsAvailable = createSelector(getIdentityActions, actions => actions.size > 0)

export const isShowControlPanel = createSelector(
  [getIdentityActions, getActivityName],
  (actions, activityName) => actions.size > 0 && activityName
)

export const hasAction = createSelector(getIdentityActions, actions => action => actions.has(action))

export const isFirstActionFinished = createSelector(
  [hasAction, getIdentityDay],
  (hasAction, day) => day > 1 || (day === 1 && !hasAction(COMMIT))
)

export const getIdentityActivitiesHistory = createSelector(getIdentity, identity => identity.get('activitiesHistory'))

export const getIdentityActivitiesHistoryItems = createSelector(getIdentityActivitiesHistory, history =>
  history.get('items')
)

export const getIdentityActivitiesHistoryPagination = createSelector(getIdentityActivitiesHistory, history =>
  history.get('pagination')
)

export const getIdentityActivitiesHistoryPage = createSelector(getIdentityActivitiesHistoryPagination, pagination =>
  pagination.get('page')
)

export const getIdentityActivitiesHistoryCount = createSelector(getIdentityActivitiesHistoryPagination, pagination =>
  pagination.get('count')
)

export const getIdentityActivitiesHistoryLimit = createSelector(getIdentityActivitiesHistoryPagination, pagination =>
  pagination.get('limit')
)

export const getIdentityActivitiesHistoryIsPageFirst = createSelector(
  getIdentityActivitiesHistoryPage,
  page => page === 0
)

export const getIdentityActivitiesHistoryIsPageLast = createSelector(
  getIdentityActivitiesHistoryPage,
  getIdentityActivitiesHistoryCount,
  getIdentityActivitiesHistoryLimit,
  (page, count, limit) => {
    const pagesTotal = count % limit ? Math.floor(count / limit) : Math.floor(count / limit) - 1
    return page === pagesTotal
  }
)

export const getIdentityActivitiesHistoryIsPageSingle = createSelector(
  getIdentityActivitiesHistoryIsPageFirst,
  getIdentityActivitiesHistoryIsPageLast,
  (isFirst, isLast) => isFirst && isLast
)

export const getIdentityPending = createSelector(getIdentity, identity => identity.get('pending'))

export const getIdentityPendingGetProgress = createSelector(getIdentityPending, pending => pending.get('getProgress'))

export const getIdentityPendingGetProfile = createSelector(getIdentityPending, pending => pending.get('getProfile'))

export const getIdentityPendingEmailQuery = createSelector(getIdentityPending, pending => pending.get('getEmailQuery'))

export const getIdentityPendingGetHistory = createSelector(getIdentityPending, pending => pending.get('getHistory'))

export const activeProTrackLevel = createSelector(getIdentity, identity => identity.get('activeLevel'))

export const getIdentityActiveProTrackName = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('proTrackName')
)

export const isIdentityActiveProTrackAutoEnroll = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('autoEnroll')
)

export const getIdentityActiveProTrackLevelName = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('name')
)

export const getIdentityActiveProTrackWithLevelName = createSelector(
  getIdentityActiveProTrackName,
  getIdentityActiveProTrackLevelName,
  (proTrackName, levelName) => `${proTrackName || ''}${levelName ? ' (' + levelName + ')' : ''}`
)

export const getIdentityActiveProTrackId = createSelector(activeProTrackLevel, activeLevel => activeLevel.get('id'))

export const getIdentityProgress = createSelector(getIdentity, identity => identity.get('progress'))

export const getIdentityProgressAll = createSelector(getIdentityProgress, progress => progress.get('all'))

export const getIdentityProgressCurrent = createSelector(getIdentityProgress, progress => progress.get('current'))

export const getIdentityProgressAllCompletedCount = createSelector(getIdentityProgressAll, all =>
  all.get('completedCount')
)

export const getIdentityProgressAllStreakCurrent = createSelector(getIdentityProgressAll, all =>
  all.get('streakCurrent')
)

export const getIdentityProgressCurrentStreakCurrent = createSelector(getIdentityProgressCurrent, current =>
  current.get('streakCurrentForLevel')
)

export const getIdentityProgressCurrentStreakMax = createSelector(getIdentityProgressCurrent, current =>
  current.get('streakMaxForLevel')
)

export const getIdentityProgressCurrentCompletedCount = createSelector(getIdentityProgressCurrent, current =>
  current.get('completedCount')
)

export const getCurrentProTrackProgress = createSelector(getIdentityProgressCurrent, current =>
  current.get('proTrackProgress')
)

export const getCurrentProTrackProgressDay = createSelector(getIdentityProgressCurrent, current =>
  current.get('progressDayForLevel')
)

export const getCurrentProTrackProgressLevelLength = createSelector(getIdentityProgressCurrent, current =>
  current.get('proTrackLevelLength')
)

export const getProgressCirclePercentages = createSelector(
  getCurrentProTrackProgressDay,
  getCurrentProTrackProgressLevelLength,
  (currentDay, levelLength) => {
    const result = currentDay / levelLength * 100
    return isNaN(result) ? 0 : result
  }
)

export const getIdentityActivitiesHistorySliced = createSelector(
  getIdentityActivitiesHistoryItems,
  getCurrentProTrackProgressDay,
  (activities, day) => activities.slice(0, day)
)

export const getIdentityPendingForStatsBlock = createSelector(
  getIdentityPendingGetProgress,
  getIdentityPendingGetProfile,
  (getProgressPending, getProfilePending) => getProgressPending || getProfilePending
)

export const getIdentityProTrackStartDate = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('startDate')
)

export const getShowUserProTrackStartBlock = createSelector(
  [getCurrentProTrackProgressDay, getIdentityStage, getActivityId],
  (day, stage, activityId) => day === 0 && stage === STAGES.PROCEED && !activityId
)

export const getShowUserOutOfOfficeBlock = createSelector(
  getIdentityDaysOff,
  dayoffs => !!dayoffs.some(d => moment().isBetween(d.get('startDate'), d.get('endDate')))
)

export const getIdentityProTrackStartBusinessDate = createSelector(
  getIdentityProTrackStartDate,
  getIdentityTimezone,
  startDate => {
    return startDate
  }
)

const surveyLinkData = createSelector(
  [getIdentity, getIdentityOrganization],
  (identity, organization) =>
    `?organization=${organization.get('name')}` +
    `&domain=${organization.get('domain')}` +
    `&email=${identity.get('email')}` +
    `&name=${identity.get('firstName') + identity.get('lastName')}`
)

export const beginSurveyLink = createSelector(
  [activeProTrackLevel, surveyLinkData],
  (proTrackLevel, surveyLinkData) => {
    if (proTrackLevel.get('preSurveyLink')) {
      return encodeURI(
        `${proTrackLevel.get('preSurveyLink')}${surveyLinkData}` +
          `&protrack=${proTrackLevel.get('proTrackName')}` +
          `&userprotracklevelid=${proTrackLevel.get('userProTrackLevelId')}` +
          `&type=begin`
      )
    }
    return false
  }
)

export const endSurveyLink = createSelector([activeProTrackLevel, surveyLinkData], (proTrackLevel, surveyLinkData) => {
  return encodeURI(
    `${proTrackLevel.get('postSurveyLink')}${surveyLinkData}` +
      `&protrack=${proTrackLevel.get('proTrackName')}` +
      `&userprotracklevelid=${proTrackLevel.get('userProTrackLevelId')}` +
      `&type=end`
  )
})

export const isPostSurveyLink = createSelector(
  activeProTrackLevel,
  previousProTrack => !!previousProTrack.get('postSurveyLink')
)

export const isBeginSurveySubmitted = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('beginSurveySubmitted')
)

export const isEndSurveySubmitted = createSelector(activeProTrackLevel, activeLevel =>
  activeLevel.get('endSurveySubmitted')
)

export const isShouldShowProTrackRatingBlock = createSelector(
  [activeProTrackLevel, isStageIsDone],
  (activeProTrackLevel, isDone) => {
    return (
      isDone &&
      activeProTrackLevel.get('id') &&
      activeProTrackLevel.get('endSurveyRequested') &&
      !activeProTrackLevel.get('rated') &&
      !activeProTrackLevel.get('stopped')
    )
  }
)

export const isShouldShowCongratulation = createSelector(
  [activeProTrackLevel, isStageIsDone],
  (previousProTrack, isDone) => {
    return isDone && previousProTrack.get('endSurveyRequested') === false && !previousProTrack.get('stopped')
  }
)

export const isShowHasIrregularWorkWeekBlock = createSelector(
  [getIdentity, getIdentityOrganization, getIdentityDay, getActionType],
  // isWorkWeekTouched must be strong compared with false in order to avoid unnecessary showing the block, when it is null
  (identity, org, day, action) =>
    org.get('hasIrregularWorkWeek') &&
    day >= 1 &&
    [COMMIT].includes(action) &&
    identity.get('isWorkWeekTouched') === false
)

export const isShowStoryForm = createSelector(
  [getIdentityOrganization, getActionType, getActivityQuestion, getHasStory],
  (organization, action, question, hasStory) => {
    return organization.get('stories') && action === DID && question && !hasStory
  }
)

export const isShowStoryFormForYesterdayActivity = createSelector(
  [getIdentityOrganization, getIdentityLastUserActivity, getActionType],
  (organization, lastActivity, actionTypeTodayActivity) => {
    return (
      organization.get('stories') &&
      lastActivity.get('actionType') === DID &&
      actionTypeTodayActivity !== DID &&
      actionTypeTodayActivity !== DIDNOT &&
      lastActivity.get('question') &&
      !lastActivity.get('hasStory')
    )
  }
)

export const isShowDonePanel = createSelector(
  [getIdentityOrganization, getActionType, getIsRated, getActivityQuestion, getHasStory],
  (organization, action, isRated, question, hasStory) =>
    organization.get('stories') && question ? hasStory && action === DID && !isRated : action === DID && !isRated
)

export const isShowDonePanelForYesterdayActivity = createSelector(
  [getIdentityOrganization, getIdentityLastUserActivity, getActionType],
  (organization, lastActivity, actionTypeTodayActivity) => {
    const isActionTypeEqualToDid = lastActivity.get('actionType') === DID
    const isRated = lastActivity.get('isRated')
    const isNotFinishedTodayActivity = actionTypeTodayActivity !== DID && actionTypeTodayActivity !== DIDNOT
    return organization.get('stories') && lastActivity.get('question')
      ? lastActivity.get('hasStory') && isActionTypeEqualToDid && !isRated && isNotFinishedTodayActivity
      : isActionTypeEqualToDid && !isRated && isNotFinishedTodayActivity
  }
)

export const isOrganizationHasStories = createSelector(getIdentityOrganization, organization =>
  organization.get('stories')
)

export const isSocialActivatedForIdentityOrganization = createSelector(getIdentityOrganization, organization =>
  organization.get('social')
)

export const isOrganizationHasReactions = createSelector(
  getIdentityOrganization,
  organization => organization.get('stories') && organization.get('reactions')
)

export const isOrganizationHasPeerInvites = createSelector(getIdentityOrganization, organization =>
  organization.get('peerInvites')
)

export const isTokenSendToEmail = createSelector(getIdentity, identity => identity.get('isTokenSendToEmail'))

export const isTokenSendToPhone = createSelector(getIdentity, identity => identity.get('isTokenSendToPhone'))

export const getOnboardingProTracks = createSelector(getIdentity, identity => identity.get('onboardingLevels'))

export const getOnboardingNewProTrackLevels = createSelector(getOnboardingProTracks, levels => levels.get('new'))

export const getOnboardingCurrentProTrack = createSelector(getOnboardingProTracks, protracks =>
  protracks.get('current')
)

export const getOnboardingPreviousProTracks = createSelector(getOnboardingProTracks, protracks =>
  protracks.get('previous')
)

export const oneSignalAppId = createSelector(getIdentity, identity => identity.get('oneSignalAppId'))

export const isDesktopNotificationsAvailable = createSelector(getIdentity, identity => {
  // TODO: uncomment when oneSignalAppId implemented
  // identity.get('oneSignalAppId')
  return false
})

export const isSMSAvailable = createSelector(getIdentityOrganization, organization => organization.get('sms'))

export const getFeaturesNotification = createSelector(getIdentity, identity => identity.get('isShowFeatureInfo'))

export const isShowFeatureSms = createSelector(
  getFeaturesNotification,
  isSMSAvailable,
  (features, sms) => features.get('sms') && sms
)

export const isShowFeatureDot = createSelector(getFeaturesNotification, features => features.get('dot'))

export const getShowSmsTooltip = createSelector(getFeaturesNotification, features => features.get('smsTooltip'))

export const getShowStoryTooltip = createSelector(getFeaturesNotification, features => features.get('stories'))

export const isHasIrregularWorkWeek = createSelector(getIdentityOrganization, org => org.get('hasIrregularWorkWeek'))

export const getIsSprintsActive = createSelector(getIdentityOrganization, org => org.get('isSprintsActive'))

export const getIdentitySprint = createSelector(getIdentity, identity => identity.get('sprint'))

export const getSprintTitle = createSelector(getIdentitySprint, sprint => sprint.get('title'))

export const getUserSprint = createSelector(getIdentitySprint, sprint => sprint.get('user'))

export const getIdentitySprintResult = createSelector([getIdentitySprint, getUserSprint], (sprint, userSprint) => {
  const organization = sprint.get('organization')
  const result = {
    userGoalAchieved: userSprint.achievedPoints >= userSprint.goal,
    orgGoalAchieved: organization.achievedPoints >= organization.goal,
    badge: sprint.get('badge'),
    orgGoalAchievedMessage: organization.goalAchievedMessage,
    userGoalAchievedMessage: userSprint.goalAchievedMessage,
  }
  return result
})

export const getIsBadgeNotAchieved = createSelector(getUserSprint, userSprint => {
  return userSprint.achievedPoints < userSprint.goal
})

export const getIdentityBadges = createSelector([getIdentity, getIsBadgeNotAchieved], (identity, isNotAchieved) => {
  const badges = identity.get('badges')
  const result = badges
    ? {
        achievedBadges: badges.achievedBadges,
        activeBadge: badges.activeBadge
          ? {
              ...badges.activeBadge,
              isNotAchieved,
            }
          : null,
      }
    : null

  return result
})
